import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Shield, User } from 'lucide-react';
import HeaderBar from '@/components/HeaderBar';
import AccountSidebar from '@/components/AccountSidebar';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SecurityPage({ token, user, logout }) {
    const navigate = useNavigate();
    
    // Name update state
    const [name, setName] = useState(user?.name || '');
    const [updatingName, setUpdatingName] = useState(false);

    useEffect(() => {
        if (user) {
            setName(user.name || '');
        }
    }, [user]);

    const handleUpdateName = async (e) => {
        e.preventDefault();
        
        if (!name.trim()) {
            toast.error('Please enter your name');
            return;
        }

        if (name === user.name) {
            toast.error('New name is same as current name');
            return;
        }

        setUpdatingName(true);

        try {
            await axios.put(
                `${API}/api/auth/update-name`,
                { name: name.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Name updated successfully!');
            // Reload the page to reflect changes
            window.location.reload();
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to update name';
            toast.error(message);
        } finally {
            setUpdatingName(false);
        }
    };

    if (!user) {
        return <div className="flex items-center justify-center h-screen">Loading...</div>;
    }

    return (
        <div className="settings-page">
            {/* Header Bar */}
            <HeaderBar user={user} logout={logout} />

            {/* Settings Header */}
            <div className="settings-header">
                <div className="settings-header-content settings-header-row">
                    <h1 className="settings-title">Security Settings</h1>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/')}
                        className="back-button"
                    >
                        ← Back to dashboard
                    </Button>
                </div>
            </div>

            <div className="settings-container">
                <AccountSidebar token={token} />

                {/* Main Content */}
                <div className="settings-main">
                    <div className="settings-content">
                        {/* Security Hero Header */}
                        <div className="subscription-hero">
                            <div className="subscription-hero__icon">
                                <Shield className="w-5 h-5" />
                            </div>
                            <div className="subscription-hero__text">
                                <h2 className="subscription-hero__title">Security</h2>
                                <p className="subscription-hero__subtitle">Manage your profile details and account security.</p>
                            </div>
                        </div>

                        {/* Update Name Section */}
                        <div className="pref-section">
                            <div className="pref-section__header">
                                <div className="pref-section__icon">
                                    <User className="w-4 h-4" />
                                </div>
                                <div className="pref-section__heading">
                                    <h3 className="pref-section__title">Your name</h3>
                                    <p className="pref-section__subtitle">This name is shown on your profile and in team activity.</p>
                                </div>
                            </div>

                            <form onSubmit={handleUpdateName} className="pref-form">
                                <div className="form-group">
                                    <label className="form-label">Name</label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Enter your name"
                                        className="form-input"
                                        required
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    disabled={updatingName}
                                    className="pref-submit-btn"
                                >
                                    {updatingName ? 'Updating...' : 'Change name'}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
